import { LazoCheckbox } from "@/components/common/form/LazoCheckbox";
import { CommonStyledSelect, CommonStyledSelectMenuProps } from "@/components/utils/SelectStyles";
import { Colors } from "@/enums/Colors";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { Box, MenuItem, Typography } from "@mui/material";
import { ChangeEvent, useMemo } from "react";
import { HeaderCells } from "./CommonTable";

interface CommonTableMobileHeaderProps {
  hideCheckboxes?: boolean;
  selectedCount: number;
  childrenLength: number;
  onAllCheckboxClick: (event: ChangeEvent<HTMLInputElement>, value: boolean) => void;
  onSortSelection: (columnId: string, newOrder?: "asc" | "desc") => void;
  headerCells: HeaderCells[];
  order: "asc" | "desc";
  orderBy: string;
}

export function CommonTableMobileHeader({
  hideCheckboxes,
  selectedCount,
  childrenLength,
  onAllCheckboxClick,
  onSortSelection,
  headerCells,
  order,
  orderBy,
}: CommonTableMobileHeaderProps) {
  const sortableColumns = useMemo(() => {
    return headerCells.filter((cell) => !cell.hideOrderBy);
  }, [headerCells]);

  function handleColumnChange(event: any) {
    const columnId = event.target.value;
    onSortSelection(columnId, order); // Keep current sort order
  }

  function handleSortDirectionToggle() {
    const newOrder = order === "asc" ? "desc" : "asc";
    onSortSelection(orderBy, newOrder);
  }

  if (hideCheckboxes && !sortableColumns.length) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "1rem",
        padding: "0.5rem",
        backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
        borderRadius: "0.75rem",
        border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
      }}
    >
      {!hideCheckboxes && (
        <LazoCheckbox
          checked={selectedCount == childrenLength && childrenLength !== 0}
          onChange={onAllCheckboxClick}
          sx={{
            "&.MuiCheckbox-root": {
              padding: "0.25rem",
            },
            "&.Mui-checked": {
              color: Colors.BUTTON_PURPLE,
            },
            "&.Mui-disabled": {
              color: Colors.ALMOST_WHITE,
            },
          }}
        />
      )}
      <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
        {sortableColumns.length > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            {/* Sort Direction Toggle Button */}
            <Box
              onClick={handleSortDirectionToggle}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2.5rem",
                height: "2.5rem",
                borderRadius: "0.5rem",
                border: `1px solid ${Colors.STROKE_DARK_GRAY}`,
                backgroundColor: "white",
                cursor: "pointer",
                transition: "border-color 0.3s",
                "&:hover": {
                  borderColor: Colors.STROKE_FOR_HOVERS_6C6C77,
                },
              }}
            >
              {order === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            </Box>

            {/* Column Selection Dropdown */}
            <CommonStyledSelect
              value={orderBy}
              onChange={handleColumnChange}
              MenuProps={CommonStyledSelectMenuProps}
              displayEmpty
              sx={{
                minWidth: "120px",
                height: "2.5rem",
              }}
            >
              {sortableColumns.map((column) => (
                <MenuItem key={column.id} value={column.id}>
                  <Typography>{column.label}</Typography>
                </MenuItem>
              ))}
            </CommonStyledSelect>
          </Box>
        )}
      </Box>
    </Box>
  );
}
